import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { ArrowLeft, Monitor, MapPin, Calendar, Home, BookOpen, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminServicesPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, token } = useAuth();
  const [visibility, setVisibility] = useState({
    julie_online: true,
    julie_greven: true,
    julie_weekend_external: true,
    julie_weekend_greven: true,
    julie_theorie_clinic: true,
    oscar_greven: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email) && token) {
      fetchVisibility();
    }
  }, [user, token]);

  const fetchVisibility = async () => {
    try {
      const response = await axios.get(`${API}/admin/services/visibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisibility(response.data);
    } catch (error) {
      console.error('Error fetching visibility:', error);
      toast.error('Kunne ikke hente indstillinger');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = async (serviceKey, currentValue) => {
    setSaving(prev => ({ ...prev, [serviceKey]: true }));
    try {
      const params = new URLSearchParams();
      params.append(serviceKey, (!currentValue).toString());
      
      await axios.put(`${API}/admin/services/visibility?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVisibility(prev => ({ ...prev, [serviceKey]: !currentValue }));
      toast.success(`${!currentValue ? 'Aktiveret' : 'Deaktiveret'}`);
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Kunne ikke opdatere indstilling');
    } finally {
      setSaving(prev => ({ ...prev, [serviceKey]: false }));
    }
  };

  const julieServices = [
    {
      key: 'julie_online',
      title: 'Pivo Live Lesson',
      subtitle: 'Online undervisning via Pivo',
      icon: Monitor
    },
    {
      key: 'julie_greven',
      title: 'Live i Heiden',
      subtitle: 'Undervisning på stedet',
      icon: MapPin
    },
    {
      key: 'julie_weekend_external',
      title: 'Weekendkursus (Udkørende)',
      subtitle: 'Kursus hos kunden',
      icon: Calendar
    },
    {
      key: 'julie_weekend_greven',
      title: 'Weekendkursus i Heiden',
      subtitle: 'Kursus på JCP facility',
      icon: Home
    },
    {
      key: 'julie_theorie_clinic',
      title: 'Teori Klinik',
      subtitle: 'Teoretiske workshops',
      icon: BookOpen
    }
  ];

  const oscarServices = [
    {
      key: 'oscar_greven',
      title: 'Live i Heiden',
      subtitle: 'Undervisning på stedet',
      icon: MapPin
    }
  ];

  if (authLoading || !user || loading) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '120px 2rem', textAlign: 'center' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const ServiceToggle = ({ service, isEnabled }) => {
    const Icon = service.icon;
    const isSaving = saving[service.key];
    
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          background: isEnabled ? '#f8fdf8' : '#fafafa',
          borderRadius: '10px',
          border: `1px solid ${isEnabled ? '#c8e6c9' : '#e0e0e0'}`,
          marginBottom: '0.75rem',
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '45px',
            height: '45px',
            borderRadius: '10px',
            background: isEnabled ? '#e8f5e9' : '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon size={22} color={isEnabled ? '#4caf50' : '#999'} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: isEnabled ? '#333' : '#666' }}>
              {service.title}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#888' }}>
              {service.subtitle}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => toggleService(service.key, isEnabled)}
          disabled={isSaving}
          data-testid={`toggle-${service.key}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.6rem 1.2rem',
            background: isEnabled ? '#4caf50' : '#e0e0e0',
            color: isEnabled ? 'white' : '#666',
            border: 'none',
            borderRadius: '25px',
            cursor: isSaving ? 'wait' : 'pointer',
            fontWeight: 500,
            fontSize: '0.9rem',
            transition: 'all 0.2s ease',
            minWidth: '100px'
          }}
        >
          {isSaving ? (
            'Gemmer...'
          ) : isEnabled ? (
            <>
              <Eye size={16} />
              Online
            </>
          ) : (
            <>
              <EyeOff size={16} />
              Skjult
            </>
          )}
        </button>
      </div>
    );
  };

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <ArrowLeft size={20} />
              Tilbage til Admin
            </button>
            
            <h1 style={{ fontSize: '1.8rem', color: '#333', marginBottom: '0.5rem' }}>
              Tjenester Synlighed
            </h1>
            <p style={{ color: '#666' }}>
              Vælg hvilke tjenester der skal vises for kunderne på hjemmesiden
            </p>
          </div>

          {/* Julie Services */}
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              marginBottom: '1.25rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #eee'
            }}>
              <img
                src="https://customer-assets.emergentagent.com/job_eduslots-6/artifacts/yr6uovu2_Julie.jpg"
                alt="Julie Christiansen"
                style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
              />
              <div>
                <h2 style={{ fontSize: '1.2rem', margin: 0, color: '#333' }}>Julie Christiansen</h2>
                <p style={{ fontSize: '0.85rem', color: '#888', margin: 0 }}>5 tjenester</p>
              </div>
            </div>
            
            {julieServices.map(service => (
              <ServiceToggle 
                key={service.key} 
                service={service} 
                isEnabled={visibility[service.key]} 
              />
            ))}
          </div>

          {/* Oscar Services */}
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              marginBottom: '1.25rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #eee'
            }}>
              <img
                src="https://customer-assets.emergentagent.com/job_eduslots-6/artifacts/95ukofjv_thumbnail_image0%20%281%29.jpg"
                alt="Óskar Erik Kristjánsson"
                style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
              />
              <div>
                <h2 style={{ fontSize: '1.2rem', margin: 0, color: '#333' }}>Óskar Erik Kristjánsson</h2>
                <p style={{ fontSize: '0.85rem', color: '#888', margin: 0 }}>1 tjeneste</p>
              </div>
            </div>
            
            {oscarServices.map(service => (
              <ServiceToggle 
                key={service.key} 
                service={service} 
                isEnabled={visibility[service.key]} 
              />
            ))}
          </div>

          {/* Info */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#fff3e0',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#e65100'
          }}>
            <strong>Tip:</strong> Når en tjeneste er "Skjult", kan kunderne ikke se den på hjemmesiden. 
            Du kan altid aktivere den igen senere.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminServicesPage;
